import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Copy, Download, LifeBuoy, Mic, Puzzle, RefreshCw, ShieldCheck,
} from 'lucide-react';

// Outils → Extension Réunions : télécharger la dernière version de l'extension Chrome et l'installer à la main.
// Le zip et version.json sont dans public/extension (construits par integrations/chrome_extension/package.py --site).
// L'extension (≥ 1.1.6) écrit sa version sur les pages Deepflow (data-deepflow-extension) : la page dit si elle est
// à jour.

const BASE = `${process.env.PUBLIC_URL || ''}/extension`;

export const compareVersions = (a, b) => {
  const left = String(a || '').split('.').map((n) => Number(n) || 0);
  const right = String(b || '').split('.').map((n) => Number(n) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff) return diff > 0 ? 1 : -1;
  }
  return 0;
};

const browserOf = (agent = navigator.userAgent || '') => {
  if (/Edg\//.test(agent)) return { name: 'Edge', page: 'edge://extensions', supported: true };
  if (/Firefox\//.test(agent)) return { name: 'Firefox', page: null, supported: false };
  if (/Chrome\//.test(agent) || /Chromium\//.test(agent)) return { name: 'Chrome', page: 'chrome://extensions', supported: true };
  if (/Safari\//.test(agent)) return { name: 'Safari', page: null, supported: false };
  return { name: 'ce navigateur', page: 'chrome://extensions', supported: true };
};

const useInstalledVersion = () => {
  const read = () => document.documentElement.dataset.deepflowExtension || null;
  const [installed, setInstalled] = useState(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setInstalled(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-deepflow-extension'] });
    return () => observer.disconnect();
  }, []);
  return installed;
};

const CopyField = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 py-0.5 pl-2 pr-1 align-middle font-mono text-[13px] text-slate-800">
      {value}
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }).catch(() => {});
        }}
        className="rounded-md px-1.5 py-0.5 font-sans text-[11px] font-semibold text-slate-500 hover:bg-white hover:text-slate-800"
        title="Copier l'adresse"
      >
        {copied ? 'Copié' : <Copy size={12} />}
      </button>
    </span>
  );
};

const Card = ({ icon: Icon, title, children }) => (
  <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
    <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
      {Icon && <Icon size={18} className="text-slate-400" />}
      {title}
    </h2>
    {children}
  </section>
);

const Steps = ({ items }) => (
  <ol className="space-y-3">
    {items.map((item, index) => (
      <li key={index} className="flex gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">{index + 1}</span>
        <div className="min-w-0 pt-0.5 text-sm leading-relaxed text-slate-700">{item}</div>
      </li>
    ))}
  </ol>
);

const kilobytes = (size) => (size ? `${Math.max(1, Math.round(size / 1024))} Ko` : '');

const ExtensionPage = () => {
  const navigate = useNavigate();
  const [latest, setLatest] = useState(null);
  const [failed, setFailed] = useState(false);
  const installed = useInstalledVersion();
  const browser = useMemo(() => browserOf(), []);

  useEffect(() => {
    fetch(`${BASE}/version.json`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('version'))))
      .then(setLatest)
      .catch(() => setFailed(true));
  }, []);

  const upToDate = installed && latest && compareVersions(installed, latest.version) >= 0;
  const extensionsPage = browser.page || 'chrome://extensions';

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:px-0">
      <button type="button" onClick={() => navigate('/tools')} className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft size={15} /> Outils
      </button>

      <header className="flex items-start gap-4">
        <img src={`${BASE}/icon128.png`} alt="" className="hidden h-14 w-14 sm:block" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">Extension Réunions</h1>
          <p className="mt-1 text-[15px] leading-relaxed text-slate-600">
            Enregistre tes réunions Google Meet et Teams (navigateur) dans Deepflow : synthèse rangée dans le bon projet,
            points à aborder pendant l'appel et notes de Nexus en direct.
          </p>
        </div>
      </header>

      {!browser.supported && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          {browser.name} n'est pas pris en charge pour l'instant : installe l'extension dans Chrome ou Edge.
        </p>
      )}

      <section className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-indigo-50 via-white to-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">{latest?.name || 'Deepflow Réunions'}</p>
            <p className="text-sm text-slate-500">
              {latest ? `Version ${latest.version} · ${kilobytes(latest.size)}` : failed ? 'Version indisponible' : 'Chargement…'}
              {latest?.minimum_chrome_version ? ` · Chrome ou Edge ${latest.minimum_chrome_version}+` : ''}
            </p>
          </div>
          <a
            href={`${BASE}/${latest?.file || 'deepflow-reunions.zip'}`}
            download={latest ? `deepflow-reunions-${latest.version}.zip` : 'deepflow-reunions.zip'}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-700"
          >
            <Download size={16} /> Télécharger l'extension
          </a>
        </div>
        <div className="mt-4 border-t border-slate-100 pt-3 text-sm">
          {upToDate ? (
            <p className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 size={15} /> Installée dans ce navigateur ({installed}) : à jour.</p>
          ) : installed && latest ? (
            <p className="flex items-center gap-1.5 font-medium text-amber-700">
              <RefreshCw size={15} /> Installée : {installed}. La version {latest.version} est disponible : voir « Mettre à jour » plus bas.
            </p>
          ) : (
            <p className="text-slate-500">
              Pas détectée dans ce navigateur (ou version antérieure à 1.1.6, qui ne se signale pas).
            </p>
          )}
        </div>
      </section>

      <Card icon={Puzzle} title="Installer (2 minutes)">
        <Steps items={[
          <>Télécharge le zip et <b>décompresse-le dans un dossier que tu gardes</b>, par exemple <i>Documents › Deepflow-extension</i>.
            Le navigateur lit l'extension dans ce dossier : ne le supprime pas.</>,
          <>Ouvre la page des extensions : colle <CopyField value={extensionsPage} /> dans la barre d'adresse
            {browser.name === 'Edge' ? '' : <> (sous Edge : <CopyField value="edge://extensions" />)</>}.</>,
          <>Active le <b>Mode développeur</b> : interrupteur en haut à droite sous Chrome, dans la colonne de gauche sous Edge.</>,
          <>Clique <b>« Charger l'extension non empaquetée »</b> et choisis le dossier décompressé (celui qui contient <code className="rounded bg-slate-100 px-1 text-[13px]">manifest.json</code>).</>,
          <>Épingle-la : l'icône <b>puzzle</b> à droite de la barre d'adresse, puis l'épingle à côté de « Deepflow Réunions ».</>,
          <>Reste connecté à Deepflow dans ce navigateur : l'extension reprend ta session toute seule. Sa fenêtre affiche
            <b> « Connecté : ton nom »</b> (sinon, recharge cette page).</>,
        ]} />
      </Card>

      <Card icon={RefreshCw} title="Mettre à jour">
        <Steps items={[
          <>Télécharge la nouvelle version et décompresse-la <b>dans le même dossier</b>, en remplaçant les fichiers.</>,
          <>Sur <CopyField value={extensionsPage} />, clique <b>↻</b> sur la carte « Deepflow Réunions ».</>,
          <>Recharge les onglets Meet ou Teams déjà ouverts. Cette page indique quand ta version a vieilli.</>,
        ]} />
      </Card>

      <Card icon={Mic} title="À l'usage">
        <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
          <li>Dans une réunion <b>Meet</b> ou <b>Teams</b> (navigateur), la pastille <b>« Enregistrer dans Deepflow »</b> apparaît en haut à gauche :
            choisis le projet (souvent proposé d'après l'agenda), puis <b>Démarrer</b>. Le navigateur demande de partager l'onglet :
            accepte en laissant <b>« Partager l'audio de l'onglet »</b> activé.</li>
          <li>Le panneau <b>Points à aborder</b> reprend les points notés dans Deepflow pour ce projet ; tu les coches ou en ajoutes pendant l'appel.</li>
          <li>Avec <b>Nexus en direct</b> (si activé pour toi), Nexus coche les points vraiment abordés et prend des notes.</li>
          <li><b>Terminer</b>, ou quitte simplement la réunion : l'envoi se termine tout seul, la synthèse arrive dans le projet.</li>
          <li>Teams en <b>application de bureau</b> n'est pas couvert : utilise le bouton micro de Deepflow.</li>
        </ul>
      </Card>

      <Card icon={LifeBuoy} title="Ça ne marche pas ?">
        <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
          <li><b>Pas de pastille dans la réunion</b> : recharge l'onglet Meet ou Teams (ceux ouverts avant l'installation ne la voient pas).</li>
          <li><b>« Non connecté à Deepflow »</b> dans la fenêtre de l'extension : ouvre Deepflow dans ce même navigateur et connecte-toi ; la session expire après 7 jours sans visite.</li>
          <li><b>Avertissement sur les extensions en mode développeur</b> au démarrage du navigateur : normal pour une extension installée à la main, garde-la activée.</li>
          <li><b>« Le manifeste est introuvable »</b> : tu as choisi le zip ou un dossier parent ; choisis le dossier qui contient directement <code className="rounded bg-slate-100 px-1 text-[13px]">manifest.json</code>.</li>
        </ul>
      </Card>

      <p className="flex items-center gap-1.5 px-1 text-xs text-slate-400">
        <ShieldCheck size={13} />
        L'audio reste chez Innowide : transcription et synthèse sur ses propres serveurs.
        <a href="/confidentialite-reunions.html" target="_blank" rel="noreferrer" className="underline hover:text-slate-600">Confidentialité</a>
      </p>
    </div>
  );
};

export default ExtensionPage;
